import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useFloating, flip, shift, autoUpdate, offset } from '@floating-ui/react-dom';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { Link } from '@/shared/components/atoms/Link';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import { Tooltip } from '@/shared/components/molecules/Tooltip';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { TabSelect } from '@/shared/components/TabSelect';
import { isSudo } from '@/shared/featureFlags';
import useSettingsStore, { type SettingsType } from '@/shared/hooks/useSettingsStore';
import useSwapRequestStore, {
  selectAutoSlippageTolerancePercent,
  selectAutoLivePriceSlippageTolerancePercent,
  selectRecommendedRetryDuration,
} from '@/shared/hooks/useSwapRequestStore';
import AlertIcon from '@/shared/icons/flip-ui-kit/small/Alert';
import KnifeIcon from '@/shared/icons/flip-ui-kit/small/Knife';
import ShieldTickIcon from '@/shared/icons/flip-ui-kit/small/ShieldTick';
import WarningTriangleIcon from '@/shared/icons/flip-ui-kit/small/WarningTriangle';
import ShieldIcon from '@/shared/icons/Shield';
import { clamp } from '@/shared/utils/numbers';
import { decimalRegex } from '@/shared/utils/regex';

const SLIPPAGE_MIN = 0.05;
const SUDO_SLIPPAGE_MIN = 0;
const SUDO_SLIPPAGE_MAX = 100;

const LPP_SLIPPAGE_WARNING_LOW = 0.25;
const LPP_SLIPPAGE_WARNING_HIGH = 2;
const LPP_SLIPPAGE_MAX = 10;

const SLIPPAGE_WARNING_LOW = 0.5;
const SLIPPAGE_WARNING_HIGH = 2.5;
const SLIPPAGE_MAX = 50;

const RETRY_MINUTES_MIN = 1;
const RETRY_MINUTES_MAX = 45;

const SlippageSettingInput = ({
  open,
  settingName,
  autoValue,
  label,
  tooltipContent,
  settingMinValue,
  settingMaxValue,
  sudoMinValue,
  sudoMaxValue,
  dataTestId,
  sudoDataTestId,
  slippageWarningLow,
  slippageWarningHigh,
}: {
  open?: boolean;
  settingName: SettingsType;
  autoValue: number | undefined;
  label: string;
  tooltipContent: string;
  settingMinValue: number;
  settingMaxValue: number;
  sudoMinValue?: number;
  sudoMaxValue?: number;
  dataTestId: string;
  sudoDataTestId?: string;
  slippageWarningLow?: number;
  slippageWarningHigh?: number;
}) => {
  const { slippageMode, getSetting, setSetting, toggleSlippageMode } = useSettingsStore();
  const [srcToken, destToken] = useSwapRequestStore((state) => [state.srcToken, state.destToken]);

  const customInputRef = useRef<HTMLInputElement | null>(null);
  const [customInputString, setCustomInputString] = useState('');

  const isSwapDeadlineMinutes = settingName === 'swapDeadlineMinutes';
  const settingValue = getSetting(settingName);

  let inputValue: string | number = '';
  if (isNotNullish(autoValue)) inputValue = slippageMode === 'auto' ? autoValue : customInputString;

  const onCustomInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (decimalRegex(isSwapDeadlineMinutes ? 0 : 2).test(e.target.value)) {
      setCustomInputString(e.target.value);
    }
  };

  const onCustomInputBlur = () => {
    if (isNotNullish(autoValue)) {
      const customInputStringToNumber = Number(customInputString);
      const min = isSudo && isNotNullish(sudoMinValue) ? sudoMinValue : settingMinValue;
      const max = isSudo && isNotNullish(sudoMaxValue) ? sudoMaxValue : settingMaxValue;

      const isInRange = customInputStringToNumber >= min && customInputStringToNumber <= max;
      const valueToValidate = (isInRange && customInputStringToNumber) || autoValue; // if user enters empty value or max < value < min, fallback with auto value

      const validated = clamp(valueToValidate, min, max);

      setCustomInputString(String(validated));
      setSetting(settingName, validated);
    }
  };

  const onInputClick = () => {
    if (isNotNullish(autoValue)) {
      if (slippageMode === 'auto') toggleSlippageMode();
      setTimeout(() => customInputRef.current?.focus(), 0); // focus after rerender
    }
  };

  useEffect(() => {
    if (open) setCustomInputString(String(settingValue));
  }, [open, settingValue]);

  const generalSlippageWarnings =
    isNotNullish(slippageWarningLow) && isNotNullish(slippageWarningHigh) ? (
      <>
        {slippageMode !== 'auto' &&
          customInputString &&
          Number(customInputString) < slippageWarningLow && (
            <Callout type="warning" size="small">
              Increase the slippage tolerance to minimise the risk for refunds.
            </Callout>
          )}
        {slippageMode !== 'auto' &&
          customInputString &&
          Number(customInputString) > slippageWarningHigh && (
            <Callout type="warning" size="small">
              Lower the slippage tolerance to minimise the risk of unfavorable price movements.
            </Callout>
          )}
        {slippageMode === 'auto' && isNotNullish(autoValue) && autoValue > slippageWarningHigh && (
          <Callout type="warning" size="small">
            {srcToken?.chainflipId === 'Flip' || destToken?.chainflipId === 'Flip'
              ? 'FLIP liquidity is limited. We suggest a higher slippage due to expected price impact.'
              : 'Suggested slippage tolerance is high. Consider lowering the amount or proceed with caution.'}
          </Callout>
        )}
      </>
    ) : null;

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex flex-row items-center justify-between font-aeonikMedium">
        <div className="flex flex-row gap-1 text-12 text-cf-light-2">
          <span>{label}</span>
          <QuestionMarkTooltip content={tooltipContent} />
        </div>
        <div className="flex flex-row items-center gap-2">
          <div
            className={classNames(
              'flex w-[60px] flex-row items-center gap-1 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 pl-1 pr-1.5 text-12 transition',
              slippageMode === 'auto' && 'text-cf-light-1',
              slippageMode === 'custom' &&
                isNotNullish(autoValue) &&
                'text-cf-white focus-within:border-cf-gray-5 focus-within:bg-cf-gray-4',
            )}
            onClick={onInputClick}
          >
            <span className="flex items-end transition">
              {isNullish(autoValue) && settingName === 'livePriceSlippageTolerancePercent' && (
                <Tooltip content="The Live Price Slippage Tolerance cannot be set because the oracle price for this asset is unavailable">
                  <AlertIcon className="text-cf-orange-2" />
                </Tooltip>
              )}
            </span>
            <input
              data-testid={dataTestId}
              inputMode={isSwapDeadlineMinutes ? 'numeric' : 'decimal'}
              className="w-0 grow bg-transparent py-1 text-left outline-none disabled:pointer-events-none"
              value={inputValue}
              disabled={slippageMode === 'auto' || isNullish(autoValue)}
              onChange={onCustomInputChange}
              onBlur={onCustomInputBlur}
              ref={customInputRef}
            />
            <span
              className={classNames(
                'transition',
                slippageMode === 'auto' ? 'text-cf-light-1' : 'text-cf-light-2',
              )}
            >
              {isSwapDeadlineMinutes ? 'min' : '%'}
            </span>
          </div>
          {isSudo && isNotNullish(autoValue) && isNotNullish(sudoMinValue) && (
            <button
              data-testid={sudoDataTestId}
              type="button"
              className="text-cf-red-2 hover:text-cf-red-1"
              onClick={() => {
                if (slippageMode === 'auto') toggleSlippageMode();
                setCustomInputString(sudoMinValue.toString());
                setSetting(settingName, sudoMinValue);
              }}
            >
              <KnifeIcon />
            </button>
          )}
        </div>
      </div>
      {!isSwapDeadlineMinutes && isNotNullish(autoValue) && generalSlippageWarnings}
    </div>
  );
};

export const SlippageProtectionSection = ({ open }: { open?: boolean }) => {
  const { slippageMode, toggleSlippageMode } = useSettingsStore();
  const autoLivePriceSlippagePercent = useSwapRequestStore(
    selectAutoLivePriceSlippageTolerancePercent,
  );
  const autoSlippagePercent = useSwapRequestStore(selectAutoSlippageTolerancePercent);
  const recommendedRetryMinutes = useSwapRequestStore(selectRecommendedRetryDuration);

  return (
    <div className="flex flex-col items-center gap-y-4">
      <div className="flex w-full flex-col gap-y-1">
        <div className="flex items-center justify-between font-aeonikMedium text-14 text-cf-white">
          <div className="flex items-center gap-x-1">
            <ShieldIcon className="text-cf-green-3" />
            Slippage Protection
          </div>
        </div>
        <span className="text-12 text-cf-light-2">
          Keeps your swap execution price under control with recommended values.{' '}
          <Link
            href="https://docs.chainflip.io/protocol/swapping-basics#slippage-protection"
            target="_blank"
            className="underline"
          >
            Learn more
          </Link>
        </span>
      </div>
      <div className="flex w-full flex-col gap-y-4">
        <div className="flex justify-center">
          <TabSelect
            tabs={[{ label: 'Auto' }, { label: 'Custom' }]}
            onSelectionChange={toggleSlippageMode}
            selectedTab={{ label: slippageMode === 'auto' ? 'Auto' : 'Custom' }}
            pillWidth={147}
            pillHeight={26}
            containerMargin={1}
            btnClasses="text-12"
          />
        </div>
        <SlippageSettingInput
          open={open}
          settingName="livePriceSlippageTolerancePercent"
          autoValue={autoLivePriceSlippagePercent}
          label="Live Price Slippage Tolerance"
          tooltipContent="A dynamic guard against the oracle price. If the executed price moves below this threshold, your swap is refunded"
          settingMinValue={SLIPPAGE_MIN}
          settingMaxValue={LPP_SLIPPAGE_MAX}
          sudoMinValue={SUDO_SLIPPAGE_MIN}
          sudoMaxValue={SUDO_SLIPPAGE_MAX}
          slippageWarningLow={LPP_SLIPPAGE_WARNING_LOW}
          slippageWarningHigh={LPP_SLIPPAGE_WARNING_HIGH}
          dataTestId="lpp-slippage-tolerance-input"
          sudoDataTestId="sudo-set-lpp-slippage"
        />
        <SlippageSettingInput
          open={open}
          settingName="slippageTolerancePercent"
          autoValue={autoSlippagePercent}
          label="Max. Slippage Tolerance"
          tooltipContent="A fixed guard set against the quoted price. Acts as a backstop in case of extreme price movements. If the price moves below this threshold, your swap is refunded"
          settingMinValue={SLIPPAGE_MIN}
          settingMaxValue={SLIPPAGE_MAX}
          sudoMinValue={SUDO_SLIPPAGE_MIN}
          sudoMaxValue={SUDO_SLIPPAGE_MAX}
          slippageWarningLow={SLIPPAGE_WARNING_LOW}
          slippageWarningHigh={SLIPPAGE_WARNING_HIGH}
          dataTestId="slippage-tolerance-input"
          sudoDataTestId="sudo-set-max-slippage"
        />
        <SlippageSettingInput
          open={open}
          settingName="swapDeadlineMinutes"
          autoValue={recommendedRetryMinutes}
          label="Swap Deadline"
          tooltipContent="Time until deposit is refunded if price exceeds slippage tolerance"
          settingMinValue={RETRY_MINUTES_MIN}
          settingMaxValue={RETRY_MINUTES_MAX}
          dataTestId="swap-deadline-input"
        />
      </div>
    </div>
  );
};

export const SlippageTolerancePopup = () => (
  <Popover>
    {({ open }) => {
      const { slippageMode, slippageTolerancePercent, livePriceSlippageTolerancePercent } =
        useSettingsStore();

      const autoLivePriceSlippageTolerancePercent = useSwapRequestStore(
        selectAutoLivePriceSlippageTolerancePercent,
      );
      const autoSlippageTolerancePercent = useSwapRequestStore(selectAutoSlippageTolerancePercent);

      let selectedLivePriceProtectionTolerancePercent;
      if (isNotNullish(autoLivePriceSlippageTolerancePercent))
        selectedLivePriceProtectionTolerancePercent =
          slippageMode === 'auto'
            ? autoLivePriceSlippageTolerancePercent
            : livePriceSlippageTolerancePercent;

      const selectedMaxSlippageTolerancePercent =
        slippageMode === 'auto' ? autoSlippageTolerancePercent : slippageTolerancePercent;

      const effectiveSlippageTolerancePercent =
        selectedLivePriceProtectionTolerancePercent ?? selectedMaxSlippageTolerancePercent;

      const { floatingStyles, refs } = useFloating({
        open,
        placement: 'bottom-end',
        middleware: [offset(8), flip(), shift({ padding: 16 })],
        whileElementsMounted: autoUpdate,
      });

      return (
        <>
          <PopoverButton
            ref={refs.setReference}
            className={classNames(
              'flex items-center gap-x-0.5 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-2 font-aeonikMedium text-12 text-cf-light-3',
              'transition duration-100 ease-out hover:bg-cf-gray-5 hover:text-cf-light-4',
              open && 'border-cf-light-1 bg-cf-gray-5 text-cf-light-4',
            )}
            data-testid="slippage-popup-toggle"
          >
            {effectiveSlippageTolerancePercent < SLIPPAGE_WARNING_LOW ||
            effectiveSlippageTolerancePercent > SLIPPAGE_WARNING_HIGH ? (
              <WarningTriangleIcon className="text-cf-orange-1" />
            ) : (
              <ShieldTickIcon
                className={classNames(
                  isNotNullish(autoLivePriceSlippageTolerancePercent) && 'text-cf-green-3',
                )}
              />
            )}

            {slippageMode === 'auto'
              ? `Auto ${effectiveSlippageTolerancePercent}%`
              : `${effectiveSlippageTolerancePercent}%`}
          </PopoverButton>
          <Transition
            as="div"
            className="relative z-10 transition-[opacity,scale]"
            enter="ease-out duration-100 scale-100"
            enterFrom="opacity-0 scale-90"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-90"
            ref={refs.setFloating}
            style={floatingStyles}
          >
            <PopoverPanel className="flex w-[320px] flex-col space-y-3 rounded-md border border-cf-gray-5 bg-cf-gray-4 p-3">
              <SlippageProtectionSection open={open} />
            </PopoverPanel>
          </Transition>
        </>
      );
    }}
  </Popover>
);
